import axios from 'axios';
import Cookies from 'universal-cookie';

// ── Estado inicial ─────────────────────────────────────────────────────────────
const initialState = {
    queueList: [],
    totalCount: 0,
    waitingCount: 0,
    reservedCount: 0,
    loading: false,
    error: null,
    claiming: false,
    enqueuing: false,
};

// ── Action Types ───────────────────────────────────────────────────────────────
const FETCH_QUEUE_START   = 'waitingQueue/FETCH_QUEUE_START';
const FETCH_QUEUE_SUCCESS = 'waitingQueue/FETCH_QUEUE_SUCCESS';
const FETCH_QUEUE_ERROR   = 'waitingQueue/FETCH_QUEUE_ERROR';
const CLAIM_START         = 'waitingQueue/CLAIM_START';
const CLAIM_DONE          = 'waitingQueue/CLAIM_DONE';
const ENQUEUE_START       = 'waitingQueue/ENQUEUE_START';
const ENQUEUE_DONE        = 'waitingQueue/ENQUEUE_DONE';

// ── Reducer ────────────────────────────────────────────────────────────────────
export default function waitingQueueReducer(state = initialState, action) {
    switch (action.type) {
        case FETCH_QUEUE_START:
            return { ...state, loading: true, error: null };

        case FETCH_QUEUE_SUCCESS:
            return {
                ...state,
                loading: false,
                queueList: action.payload.items,
                totalCount: action.payload.totalCount,
                waitingCount: action.payload.waitingCount,
                reservedCount: action.payload.reservedCount,
            };

        case FETCH_QUEUE_ERROR:
            return { ...state, loading: false, error: action.payload };

        case CLAIM_START:
            return { ...state, claiming: true };

        case CLAIM_DONE:
            return { ...state, claiming: false };

        case ENQUEUE_START:
            return { ...state, enqueuing: true };

        case ENQUEUE_DONE:
            return { ...state, enqueuing: false };

        default:
            return state;
    }
}

// ── Helpers ────────────────────────────────────────────────────────────────────
const getHeaders = () => {
    const cookies = new Cookies();
    return { Authorization: `Bearer ${cookies.get('token')}` };
};

// ── Thunks ─────────────────────────────────────────────────────────────────────

/**
 * Obtiene la cola de espera paginada con filtro opcional por línea de negocio.
 */
export const fetchWaitingQueueFromAPI = (businessLineId = null, page = 1, pageSize = 50) => async (dispatch) => {
    dispatch({ type: FETCH_QUEUE_START });
    try {
        const params = { page, pageSize };
        if (businessLineId !== null) params.businessLineId = businessLineId;

        const response = await axios.get(`${window.API_BASE_URL}/waiting-queue`, {
            params,
            headers: getHeaders(),
        });

        dispatch({
            type: FETCH_QUEUE_SUCCESS,
            payload: {
                items: response.data?.items ?? [],
                totalCount: response.data?.totalCount ?? 0,
                waitingCount: response.data?.waitingCount ?? 0,
                reservedCount: response.data?.reservedCount ?? 0,
            },
        });
    } catch (error) {
        console.error('Error fetching waiting queue:', error);
        dispatch({ type: FETCH_QUEUE_ERROR, payload: error?.message ?? 'Error al cargar la cola' });
    }
};

/**
 * Agrega una conversación a la cola de espera.
 */
export const enqueueConversationInAPI = (conversationId, notes = null) => async (dispatch) => {
    dispatch({ type: ENQUEUE_START });
    try {
        const res = await axios.post(
            `${window.API_BASE_URL}/waiting-queue/enqueue`,
            { conversationId, notes },
            { headers: getHeaders() }
        );
        return res.data;
    } catch (error) {
        console.error('Error enqueuing conversation:', error);
        throw error;
    } finally {
        dispatch({ type: ENQUEUE_DONE });
    }
};

/**
 * Reclama una entrada específica de la cola.
 */
export const claimQueueEntryInAPI = (id) => async (dispatch) => {
    dispatch({ type: CLAIM_START });
    try {
        const res = await axios.post(
            `${window.API_BASE_URL}/waiting-queue/${id}/claim`,
            {},
            { headers: getHeaders() }
        );
        return res.data;
    } catch (error) {
        console.error('Error claiming queue entry:', error);
        throw error;
    } finally {
        dispatch({ type: CLAIM_DONE });
    }
};

/**
 * Reclama la siguiente conversación disponible (FIFO, priorizando reservadas).
 * Retorna null si la cola está vacía (204).
 */
export const claimNextInAPI = (businessLineId) => async (dispatch) => {
    dispatch({ type: CLAIM_START });
    try {
        const res = await axios.post(
            `${window.API_BASE_URL}/waiting-queue/claim-next`,
            {},
            {
                params: { businessLineId },
                headers: getHeaders(),
            }
        );
        return res.status === 204 ? null : res.data;
    } catch (error) {
        if (error?.response?.status === 204) return null;
        console.error('Error claiming next in queue:', error);
        throw error;
    } finally {
        dispatch({ type: CLAIM_DONE });
    }
};

/**
 * Deriva una entrada "sin asignar" a un consultor específico (acción de supervisor).
 */
export const assignEntryToAgentInAPI = (id, agentUserId) => async (dispatch) => {
    dispatch({ type: CLAIM_START });
    try {
        const res = await axios.post(
            `${window.API_BASE_URL}/waiting-queue/${id}/assign-to`,
            { agentUserId },
            { headers: getHeaders() }
        );
        return res.data;
    } catch (error) {
        console.error('Error assigning queue entry:', error);
        throw error;
    } finally {
        dispatch({ type: CLAIM_DONE });
    }
};

/**
 * Obtiene los consultores de una línea de negocio (para el selector de derivación).
 */
export const fetchAgentsByBusinessLine = async (businessLineId) => {
    const res = await axios.get(`${window.API_BASE_URL}/assignments/agents`, {
        params: { businessLineId },
        headers: getHeaders(),
    });
    return res.data ?? [];
};

/**
 * Cancela una entrada de la cola de espera.
 */
export const cancelQueueEntryInAPI = (id) => async () => {
    try {
        await axios.post(
            `${window.API_BASE_URL}/waiting-queue/${id}/cancel`,
            {},
            { headers: getHeaders() }
        );
    } catch (error) {
        console.error('Error cancelling queue entry:', error);
        throw error;
    }
};
